import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authActivityEvents } from '../../services/authActitivityEvents';
import { itdrApi } from '../../services/api';
import adapIdMFAService from '../../services/adapIdMFAService';

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for real-time data


export const fetchAdapidActivities = createAsyncThunk(
  'adapidMfa/fetchAdapidActivities',
  async ({ page, limit, search,status }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      
      // Only add search if it's not null/undefined/empty
      if (search) {
        params.search = search;
      }
      if (status) params.status = status;

      const response = await itdrApi.get('/behaviour-logs', { params });

      return { 
        data: response.data.data.logs || [],
        pagination: {
          totalPages: response.data.data.pages,
          totalCount: response.data.data.total
        }
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load Adapid data');
    }
  }
);

export const fetchAdapidMfaOverview = createAsyncThunk(
  'adapidMfa/fetchAdapidMfaOverview',
   async (_, { getState, rejectWithValue }) => {
    try {
      const { lastFetched } = getState().adapidMfa;
      const now = Date.now();

      // Return cached data if still valid
      if (lastFetched && now - lastFetched < CACHE_DURATION) {
        return { cached: true };
      }

      const response = await adapIdMFAService.getadapidMfaOverview();
    //   console.log("fetchAdapidMfaOverview",response?.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAdapidAppLockerActivities = createAsyncThunk(
  'adapidMfa/fetchAdapidAppLockerActivities',
  async ({ page, limit, search,status }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      
      // Only add search if it's not null/undefined/empty
      if (search) {
        params.search = search;
      }
      if (status) params.status = status;

      const response = await itdrApi.get('/tenant/app/access/logs', { params });

      return { 
        data: response?.data?.data?.logs || [],
        pagination: {
          totalPages: response?.data?.data?.pages,
          totalCount: response?.data?.data?.count
        }
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load Adapid data');
    }
  }
);




const adapIdMfaSlice = createSlice({
  name: 'adapidMfa',
  initialState: {
    currentTab: 'behavioral',
    data: {
      total: 0,
      legitimate_count: 0,
      legitimate_rate: 0,
      suspicious_count:0,
      insufficient_count:0
    },
    loading: false,
    error: null,
    lastFetched: null,

    searchQuery: '',
    statusFilter: '',
    currentPage: 1,
    pageSize: 5,

    // Adapid Activities
    adapidActivities: {
      data: [], // Changed from {} to []
      loading: false,
      error: null,
      pagination: { totalPages: 0, totalCount: 0 },
    },

    adapidAppLockerActivities: {
      data: [], // Changed from {} to []
      loading: false,
      error: null,
      pagination: { totalPages: 0, totalCount: 0 },
    },

    // Selected Event (for detail view)
    selectedEvent: null,
    detailLoading: false,

  },
  reducers: {
    setActiveTab: (state, action) => {
      state.currentTab = action.payload;
      // Reset filter and pagination context when switching views
      state.searchQuery = '';
      state.statusFilter = '';
      state.currentPage = 1;
      state.selectedEvent = null;
    },
    clearStats: (state) => {
      state.data = {
        total: 0,
        legitimate_count: 0,
        legitimate_rate: 0,
        suspicious_count:0,
        insufficient_count:0
        };
      state.lastFetched = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
    // Set selected event
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
      state.detailLoading = false;
    },

    // Clear selected event
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
      state.detailLoading = false;
    },

    // Set current page (for Adapid)
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    clearAdapidCache: (state) => {
      state.adapidActivities.lastFetched = null;
    },

    setAdapidSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.currentPage = 1; 
    },
    setAdapidStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
      state.currentPage = 1;
    }
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchAdapidMfaOverview.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
    .addCase(fetchAdapidMfaOverview.fulfilled, (state, action) => {
    state.loading = false;
    if (!action.payload.cached) {
        state.data = action.payload;
        state.lastFetched = Date.now();
    }
    })
    .addCase(fetchAdapidMfaOverview.rejected, (state, action) => {
    state.loading = false;
    state.error = action.payload;
    })
    .addCase(fetchAdapidActivities.pending, (state) => {
    state.adapidActivities.loading = true;
    state.adapidActivities.error = null;
    })
    .addCase(fetchAdapidActivities.fulfilled, (state, action) => {
    state.adapidActivities.loading = false;
    state.adapidActivities.data = action.payload.data;
    state.adapidActivities.pagination = action.payload.pagination;
    })
    .addCase(fetchAdapidActivities.rejected, (state, action) => {
    state.adapidActivities.loading = false;
    state.adapidActivities.error = action.payload;
    state.adapidActivities.data = []; // Clear data on error
    })
    .addCase(fetchAdapidAppLockerActivities.pending, (state) => {
    state.adapidAppLockerActivities.loading = true;
    state.adapidAppLockerActivities.error = null;
    })
    .addCase(fetchAdapidAppLockerActivities.fulfilled, (state, action) => {
    state.adapidAppLockerActivities.loading = false;
    state.adapidAppLockerActivities.data = action.payload.data;
    state.adapidAppLockerActivities.pagination = action.payload.pagination;
    })
    .addCase(fetchAdapidAppLockerActivities.rejected, (state, action) => {
    state.adapidAppLockerActivities.loading = false;
    state.adapidAppLockerActivities.error = action.payload;
    state.adapidAppLockerActivities.data = []; // Clear data on error
    })
    },


});

export const {
  setSelectedEvent,
  clearSelectedEvent,
  setActiveTab,
  setCurrentPage,
  clearAdapidCache,
  setAdapidSearchQuery,
  setAdapidStatusFilter,
} = adapIdMfaSlice.actions;

export default adapIdMfaSlice.reducer;